import React, { useMemo, useState } from 'react';
import { Plus, CalendarDays } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useBookings, useCancelBooking, useCompleteBooking, useCreateBooking, useRescheduleBooking, useUpdateBooking } from '../../hooks/useBooking';
import { useServices } from '../../hooks/useBookingCatalog';
import BookingTable from '../../components/booking/BookingTable';
import BookingModal, { BookingModalMode } from '../../components/booking/BookingModal';
import { BookingResponseDto, BookingStatus, BOOKING_STATUS_LABELS } from '../../types/booking';
import { BookingFormValues } from '../../utils/bookingValidators';
import { useBookingSettings } from '../../hooks/useBookingSettings';

export default function BookingsPage() {
  const navigate = useNavigate();
  const { data: bookings, isLoading } = useBookings();
  const { data: services } = useServices();
  const createBooking = useCreateBooking();
  const updateBooking = useUpdateBooking();
  const rescheduleBooking = useRescheduleBooking();
  const cancelBooking = useCancelBooking();
  const completeBooking = useCompleteBooking();
  const { data: settings } = useBookingSettings();

  const [statusFilter, setStatusFilter] = useState<BookingStatus | 'All'>('All');
  const [serviceFilter, setServiceFilter] = useState('All');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<BookingModalMode>('create');
  const [activeBooking, setActiveBooking] = useState<BookingResponseDto | null>(null);

  const filteredBookings = useMemo(() => {
    return (bookings ?? []).filter((b) => {
      const bookingDateOnly = b.bookingDate.slice(0, 10);
      const matchesStatus = statusFilter === 'All' || b.status === statusFilter;
      const matchesService = serviceFilter === 'All' || b.bookingServiceId === serviceFilter;
      const matchesFrom = !dateFrom || bookingDateOnly >= dateFrom;
      const matchesTo = !dateTo || bookingDateOnly <= dateTo;
      return matchesStatus && matchesService && matchesFrom && matchesTo;
    });
  }, [bookings, statusFilter, serviceFilter, dateFrom, dateTo]);

  const openCreate = () => {
    setModalMode('create');
    setActiveBooking(null);
    setModalOpen(true);
  };

  const openEdit = (booking: BookingResponseDto) => {
    setModalMode('edit');
    setActiveBooking(booking);
    setModalOpen(true);
  };

  const openReschedule = (booking: BookingResponseDto) => {
    setModalMode('reschedule');
    setActiveBooking(booking);
    setModalOpen(true);
  };

  const handleSave = (values: BookingFormValues) => {
    const onSuccess = () => setModalOpen(false);
    const bookingDate = `${values.bookingDate}T${values.bookingTime}:00`;

    if (modalMode === 'create') {
      createBooking.mutate(
        {
          bookingServiceId: values.bookingServiceId,
          customerFirstName: values.customerFirstName,
          customerLastName: values.customerLastName,
          customerEmail: values.customerEmail,
          customerPhone: values.customerPhone,
          customerNotes: values.customerNotes,
          bookingDate,
        },
        { onSuccess }
      );
    } else if (modalMode === 'edit' && activeBooking) {
      updateBooking.mutate(
        {
          id: activeBooking.id,
          data: {
            bookingServiceId: values.bookingServiceId,
            customerFirstName: values.customerFirstName,
            customerLastName: values.customerLastName,
            customerEmail: values.customerEmail,
            customerPhone: values.customerPhone,
            customerNotes: values.customerNotes,
            bookingDate,
          },
        },
        { onSuccess }
      );
    } else if (modalMode === 'reschedule' && activeBooking) {
      rescheduleBooking.mutate({ id: activeBooking.id, data: { newBookingDate: bookingDate } }, { onSuccess });
    }
  };

  const handleCancel = (booking: BookingResponseDto) => {
    if (window.confirm(`Cancel booking ${booking.bookingReference}?`)) {
      cancelBooking.mutate({ id: booking.id, data: {} });
    }
  };

  const handleComplete = (booking: BookingResponseDto) => {
    completeBooking.mutate(booking.id);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-strivre-navy flex items-center gap-2"><CalendarDays size={22} /> Bookings</h1>
          <p className="text-sm text-strivre-mutedtext mt-1">Manage customer appointments across every service.</p>
        </div>
        <button onClick={openCreate} className="flex items-center gap-2 bg-strivre-navy text-white rounded-lg px-4 py-2.5 text-sm font-semibold hover:bg-strivre-navy/90">
          <Plus size={18} /> Create Booking
        </button>
      </div>

      <div className="bg-white border border-strivre-lightnavy rounded-lg p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div>
          <label className="block text-xs font-semibold text-strivre-mutedtext mb-1">Status</label>
          <select
            className="input-base h-10"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value === 'All' ? 'All' : (Number(e.target.value) as BookingStatus))}
          >
            <option value="All">All Statuses</option>
            {Object.entries(BOOKING_STATUS_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-strivre-mutedtext mb-1">Service</label>
          <select className="input-base h-10" value={serviceFilter} onChange={(e) => setServiceFilter(e.target.value)}>
            <option value="All">All Services</option>
            {services?.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-strivre-mutedtext mb-1">Date From</label>
          <input type="date" className="input-base h-10" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </div>
        <div>
          <label className="block text-xs font-semibold text-strivre-mutedtext mb-1">Date To</label>
          <input type="date" className="input-base h-10" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </div>
      </div>

      <BookingTable
        bookings={filteredBookings}
        loading={isLoading}
        timezone={settings?.timezone ?? 'UTC'}
        onView={(b) => navigate(`/booking/bookings/${b.id}`)}
        onEdit={openEdit}
        onReschedule={openReschedule}
        onCancel={handleCancel}
        onComplete={handleComplete}
      />

      <BookingModal
        isOpen={modalOpen}
        mode={modalMode}
        booking={activeBooking}
        services={services ?? []}
        saving={createBooking.isPending || updateBooking.isPending || rescheduleBooking.isPending}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
      />
    </div>
  );
}

